import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Event } from './models';
import { baseApi } from '@centerpiece/components';
import { project } from './project.api';

export const event = baseApi
  .enhanceEndpoints({ addTagTypes: ['Events', 'Event'] })
  .injectEndpoints({
    endpoints: builder => ({
      getEvents: builder.query<Event[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'event',
          method: 'GET',
          params: params ? params : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Event' as const, id })),
                'Events',
              ]
            : ['Events'],
      }),
      getEvent: builder.query<Event, { id: number; params?: object }>({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `event/${id}`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) => [{ type: 'Event', id: arg.id }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
        transformResponse(data: Event, meta, arg) {
          return {
            ...data,
            status_r: { ...data?.e_status_r },
            relationships: [...data?.e_relationships],
          };
        },
      }),
      createEvent: builder.mutation({
        query: request => ({
          url: 'event',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Events'],
      }),
      patchEvent: builder.mutation<Event, Event>({
        query: request => ({
          url: `event/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Event', id: arg.id },
        ],
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            event.util.updateQueryData(
              'getEvent',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, {
                  id,
                  project: pid,
                  ...patch,
                  status_r: patch.e_status_r,
                });
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(event.util.invalidateTags([{ type: 'Event', id: id }]));
          }
        },
      }),
      updateEvent: builder.mutation<Event, Event>({
        query: request => ({
          url: `event/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Event', id: arg.id },
        ],
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            event.util.updateQueryData(
              'getEvent',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, {
                  id,
                  project: pid,
                  ...patch,
                  status_r: patch.e_status_r,
                });
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(event.util.invalidateTags([{ type: 'Event', id: id }]));
          }
        },
      }),
      deleteEvent: builder.mutation<Event, number>({
        query: id => ({
          url: `event/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Event' as const, arg }, 'Events'] : ['Events'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchEventMutation,
  useGetEventsQuery,
  useGetEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useLazyGetEventQuery,
  usePrefetch: useEventsPrefetch,
} = event;
const selectEventsRoot = event.endpoints.getEvents.select({});
export const selectEvents = createSelector(
  selectEventsRoot,
  event => event.data || []
);

export const subscribeEvent = event.endpoints.getEvent.useQueryState;
